/**
 * persistence/managers/RecoveryManager.js
 *
 * Handles crash recovery on InputBoard mount.
 *
 * Recovery decision logic:
 *
 *   1. Load the session record from IndexedDB for (matchId, analystId)
 *   2. If no session record exists → no recovery needed (fresh session)
 *   3. If session exists AND (updatedAt - lastApiSyncAt) > RECOVERY_THRESHOLD_MS
 *      → There is unsaved local work the server doesn't have
 *      → Return a recovery offer to the caller
 *   4. If session exists but server is up-to-date → no recovery needed
 *
 * The caller (useSessionPersistence hook) decides what UI to show.
 * RecoveryManager never shows UI directly — it only returns data.
 *
 * Threshold explanation:
 *   RECOVERY_THRESHOLD_MS = 30 seconds.  This prevents spurious recovery
 *   prompts caused by the race between the IndexedDB write (200ms debounce)
 *   and the API push (up to 5s typing-pause debounce).  If the gap is less
 *   than 30 seconds, we assume the sync was in flight and not a real crash.
 */

import { getSession } from "./SessionManager";
import { RECOVERY_THRESHOLD_MS } from "../config";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * @typedef {object} RecoveryOffer
 * @property {boolean}  shouldRecover        true if the user should be offered recovery
 * @property {string}   localString          The unsaved local event string
 * @property {number}   localEventCount      Number of meaningful events in localString
 * @property {number}   localUpdatedAt       epoch ms of the last local write
 * @property {number}   gapMs                ms difference between local and last API sync
 * @property {string}   gapFormatted         Human-readable gap, e.g. "3 minutes ago"
 */

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Checks whether there is unsaved local work that should be offered for recovery.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<RecoveryOffer>}
 */
export async function checkForRecovery(matchId, analystId) {
  const noRecovery = {
    shouldRecover:    false,
    localString:      null,
    localEventCount:  0,
    localUpdatedAt:   null,
    gapMs:            0,
    gapFormatted:     null,
  };

  try {
    const session = await getSession(matchId, analystId);

    // No local session at all
    if (!session) return noRecovery;

    // Session has no meaningful string
    if (!session.currentString || session.currentString === "") {
      return noRecovery;
    }

    const { updatedAt, lastApiSyncAt, currentString, eventCount } = session;

    // If we never synced to API, any local data is "unsaved"
    if (!lastApiSyncAt) {
      // Only offer recovery if there's a meaningful amount of work (> 1 event)
      if (eventCount <= 1) return noRecovery;

      return {
        shouldRecover:   true,
        localString:     currentString,
        localEventCount: eventCount,
        localUpdatedAt:  updatedAt,
        gapMs:           Date.now() - updatedAt,
        gapFormatted:    _formatAge(updatedAt),
      };
    }

    // Compare local write time to last API sync time
    const gapMs = updatedAt - lastApiSyncAt;

    if (gapMs <= RECOVERY_THRESHOLD_MS) {
      return noRecovery;
    }

    return {
      shouldRecover:   true,
      localString:     currentString,
      localEventCount: eventCount,
      localUpdatedAt:  updatedAt,
      gapMs,
      gapFormatted:    _formatAge(updatedAt),
    };
  } catch (error) {
    console.warn("[RecoveryManager] checkForRecovery failed:", error.message);
    return noRecovery;
  }
}

// ─── Private ──────────────────────────────────────────────────────────────────

/**
 * Returns a human-readable relative time string.
 * e.g. "2 minutes ago", "just now", "1 hour ago"
 *
 * We implement this without a library dependency since the app already has
 * moment, dayjs, and luxon — but we want this module to be dependency-free.
 *
 * @param {number} epochMs
 * @returns {string}
 */
function _formatAge(epochMs) {
  const diffMs  = Date.now() - epochMs;
  const diffSec = Math.floor(diffMs / 1_000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr  = Math.floor(diffMin / 60);

  if (diffSec < 10)  return "just now";
  if (diffSec < 60)  return `${diffSec} seconds ago`;
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < 60)  return `${diffMin} minutes ago`;
  if (diffHr === 1)  return "1 hour ago";
  return `${diffHr} hours ago`;
}
